import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import './InterviewFeedback.css';

const InterviewFeedback = ({ 
  interview, 
  onClose, 
  onSubmitSuccess, 
  isEditing = false, 
  existingFeedback = null 
}) => {
  const [submitting, setSubmitting] = useState(false);
  
  // Ensure we have a valid interview object
  const safeInterview = interview || {};
  const safeExistingFeedback = existingFeedback || {};
  
  // Ensure interview type is one of the valid types recognized by the server
  let interviewType = safeInterview.interviewType || safeExistingFeedback.interviewType || 'DSA';
  
  // Normalize the interview type to match server expectations
  if (interviewType === 'Behavioral' || interviewType === 'Leadership') {
    interviewType = 'Behavioral/Leadership';
  }

// Initialize form state
  const [feedbackForm, setFeedbackForm] = useState({
    // Common fields
    communicationScore: '',
    strengths: '',
    areasOfImprovement: '',
    additionalComments: '',
    
    // DSA-specific fields
    codingAbility: '',
    algorithmicThinking: '',
    problemSolvingApproach: '',
    timeComplexityAnalysis: '',
    
    // System Design-specific fields
    requirementsGathering: '',
    architectureDesign: '',
    scalabilityConsiderations: '',
    tradeoffAnalysis: '',
    
    // Behavioral/Leadership-specific fields
    situationalAwareness: '',
    leadershipSkills: '',
    teamworkAbility: '',
    conflictResolution: ''
  });
  
  // Track which fields have been touched for validation
  const [touched, setTouched] = useState({
    // Common fields
    communicationScore: false,
    
    // DSA-specific fields
    codingAbility: false,
    algorithmicThinking: false,
    problemSolvingApproach: false,
    timeComplexityAnalysis: false,
    
    // System Design-specific fields
    requirementsGathering: false,
    architectureDesign: false,
    scalabilityConsiderations: false,
    tradeoffAnalysis: false,
    
    // Behavioral/Leadership-specific fields
    situationalAwareness: false,
    leadershipSkills: false,
    teamworkAbility: false,
    conflictResolution: false
  });

  // Populate form with existing feedback data if editing
  useEffect(() => {
    if (isEditing && existingFeedback) {
      const formData = {
        // Common fields
        communicationScore: existingFeedback.communicationScore || '',
        strengths: existingFeedback.strengths || '',
        areasOfImprovement: existingFeedback.areasOfImprovement || '',
        additionalComments: existingFeedback.additionalComments || '',
        
        // Initialize all type-specific fields with empty values first
        codingAbility: '',
        algorithmicThinking: '',
        problemSolvingApproach: '',
        timeComplexityAnalysis: '',
        requirementsGathering: '',
        architectureDesign: '',
        scalabilityConsiderations: '',
        tradeoffAnalysis: '',
        situationalAwareness: existingFeedback.situationalAwareness || '',
        leadershipSkills: existingFeedback.leadershipSkills || '',
        teamworkAbility: existingFeedback.teamworkAbility || '',
        conflictResolution: existingFeedback.conflictResolution || ''
      };
      
      // Add type-specific fields based on interview type
      if (existingFeedback.interviewType === 'DSA') {
        formData.codingAbility = existingFeedback.codingAbility || '';
        formData.algorithmicThinking = existingFeedback.algorithmicThinking || '';
        formData.problemSolvingApproach = existingFeedback.problemSolvingApproach || '';
        formData.timeComplexityAnalysis = existingFeedback.timeComplexityAnalysis || '';
      } else if (existingFeedback.interviewType === 'System Design') {
        formData.requirementsGathering = existingFeedback.requirementsGathering || '';
        formData.architectureDesign = existingFeedback.architectureDesign || '';
        formData.scalabilityConsiderations = existingFeedback.scalabilityConsiderations || '';
        formData.tradeoffAnalysis = existingFeedback.tradeoffAnalysis || '';
      } else if (existingFeedback.interviewType === 'Behavioral/Leadership') {
        formData.situationalAwareness = existingFeedback.situationalAwareness || '';
        formData.leadershipSkills = existingFeedback.leadershipSkills || '';
        formData.teamworkAbility = existingFeedback.teamworkAbility || '';
        formData.conflictResolution = existingFeedback.conflictResolution || '';
      }
      
      // Update form state with the populated data
      setFeedbackForm(formData);
    }
  }, [isEditing, existingFeedback]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedbackForm({
      ...feedbackForm,
      [name]: value
    });
    
    // Mark the field as touched when it changes
    if (touched.hasOwnProperty(name)) {
      setTouched({
        ...touched,
        [name]: true
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate interview object first
    if (isEditing) {
      // For editing, we need the existing feedback ID
      if (!existingFeedback || !existingFeedback._id) {
        toast.error('Invalid feedback data. Please try again or contact support.');
        return;
      }
    } else {
      // For new feedback, we need the interview ID
      if (!safeInterview._id) {
        toast.error('Invalid interview data. Please try again or contact support.');
        return;
      }
    }
    
    // Mark all required fields as touched to show validation errors
    
    setSubmitting(true);
    
    try {
      // Prepare feedback data - convert numeric fields to numbers
      const preparedFeedback = { ...feedbackForm };
      
      // Convert common numeric fields
      if (preparedFeedback.communicationScore) {
        preparedFeedback.communicationScore = Number(preparedFeedback.communicationScore);
      }
      
      // Convert type-specific numeric fields based on interview type
      if (interviewType === 'Behavioral/Leadership') {
        // Convert Behavioral/Leadership fields to numbers
        if (preparedFeedback.situationalAwareness) {
          preparedFeedback.situationalAwareness = Number(preparedFeedback.situationalAwareness);
        }
        if (preparedFeedback.leadershipSkills) {
          preparedFeedback.leadershipSkills = Number(preparedFeedback.leadershipSkills);
        }
        if (preparedFeedback.teamworkAbility) {
          preparedFeedback.teamworkAbility = Number(preparedFeedback.teamworkAbility);
        }
        if (preparedFeedback.conflictResolution) {
          preparedFeedback.conflictResolution = Number(preparedFeedback.conflictResolution);
        }
      } else if (interviewType === 'DSA') {
        // Convert DSA fields to numbers
        if (preparedFeedback.codingAbility) {
          preparedFeedback.codingAbility = Number(preparedFeedback.codingAbility);
        }
        if (preparedFeedback.algorithmicThinking) {
          preparedFeedback.algorithmicThinking = Number(preparedFeedback.algorithmicThinking);
        }
        if (preparedFeedback.problemSolvingApproach) {
          preparedFeedback.problemSolvingApproach = Number(preparedFeedback.problemSolvingApproach);
        }
        if (preparedFeedback.timeComplexityAnalysis) {
          preparedFeedback.timeComplexityAnalysis = Number(preparedFeedback.timeComplexityAnalysis);
        }
        
        // Handle legacy fields for backward compatibility
        if (preparedFeedback.codingAndDebugging) {
          preparedFeedback.codingAndDebugging = Number(preparedFeedback.codingAndDebugging);
        }
        if (preparedFeedback.problemSolvingScore) {
          preparedFeedback.problemSolvingScore = Number(preparedFeedback.problemSolvingScore);
        }
      } else if (interviewType === 'System Design') {
        // Convert System Design fields to numbers
        if (preparedFeedback.requirementsGathering) {
          preparedFeedback.requirementsGathering = Number(preparedFeedback.requirementsGathering);
        }
        if (preparedFeedback.architectureDesign) {
          preparedFeedback.architectureDesign = Number(preparedFeedback.architectureDesign);
        }
        if (preparedFeedback.scalabilityConsiderations) {
          preparedFeedback.scalabilityConsiderations = Number(preparedFeedback.scalabilityConsiderations);
        }
        if (preparedFeedback.tradeoffAnalysis) {
          preparedFeedback.tradeoffAnalysis = Number(preparedFeedback.tradeoffAnalysis);
        }
      }
      
      // Prepare payload
      const payload = {
        interviewId: interview._id,
        interviewType,
        ...preparedFeedback
      };
      
      // Remove empty legacy fields to avoid validation errors
      if (payload.codingAndDebugging === '') {
        delete payload.codingAndDebugging;
      }
      
      if (payload.problemSolvingScore === '') {
        delete payload.problemSolvingScore;
      }
      
      // Remove empty fields for other interview types
      if (interviewType !== 'DSA') {
        ['codingAbility', 'algorithmicThinking', 'problemSolvingApproach', 'timeComplexityAnalysis'].forEach(field => {
          if (payload[field] === '') delete payload[field];
        });
      }
      
      if (interviewType !== 'System Design') {
        ['requirementsGathering', 'architectureDesign', 'scalabilityConsiderations', 'tradeoffAnalysis'].forEach(field => {
          if (payload[field] === '') delete payload[field];
        });
      }
      
      if (interviewType !== 'Behavioral/Leadership') {
        ['situationalAwareness', 'leadershipSkills', 'teamworkAbility', 'conflictResolution'].forEach(field => {
          if (payload[field] === '') delete payload[field];
        });
      }

      if (isEditing) {
        // Update existing feedback
        await api.put(`/api/feedback/${safeExistingFeedback._id}`, {
          ...payload
        });
        toast.success('Feedback updated successfully');
      } else {
        // Create new feedback
        const response = await api.post('/api/feedback', payload);
        toast.success('Feedback submitted successfully');
      }
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      
      onClose();
    } catch (err) {
      let errorMessage = 'An error occurred while submitting feedback';
      
      if (err.response) {
        
        // Display validation errors from the server
        if (err.response.data.errors) {
          let errorMessages = '';
          
          if (Array.isArray(err.response.data.errors)) {
            // Handle array of errors (express-validator format)
            errorMessages = err.response.data.errors
              .map(error => `${error.param || 'Field'}: ${error.msg || error.message || 'Invalid value'}`)
              .join('\n');
          } else if (typeof err.response.data.errors === 'object') {
            // Handle object of errors
            errorMessages = Object.entries(err.response.data.errors)
              .map(([field, message]) => `${field}: ${message}`)
              .join('\n');
          }
          
          toast.error(`Validation errors:\n${errorMessages || 'Unknown validation error'}`);
        } else if (err.response.data.message) {
          // Display custom error message from the server
          toast.error(`Server error: ${err.response.data.message}`);
        } else {
          // Fallback to generic error
          toast.error(`Error submitting feedback: ${err.message}`);
        }
      } else {
        // Generic error
        toast.error(`Error submitting feedback: ${err.message}`);
      }
      
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="feedback-modal">
        <div className="feedback-modal-header">
          <h3>{isEditing ? 'Edit Interview Feedback' : 'Provide Interview Feedback'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="feedback-modal-body">
          <p>
            {isEditing 
              ? `Edit feedback for ${safeInterview.candidate?.name || 'candidate'}'s ${interviewType} interview` 
              : `Provide feedback for ${safeInterview.candidate?.name || 'candidate'}'s ${interviewType} interview`}
          </p>
          
          <form onSubmit={handleSubmit}>
            {/* Common fields for all interview types */}
            <div className="form-section">
              <h4>General Feedback</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Communication (1-10) *</label>
                  <input
                    type="number"
                    name="communicationScore"
                    value={feedbackForm.communicationScore}
                    onChange={handleChange}
                    onBlur={() => setTouched({...touched, communicationScore: true})}
                    min="1"
                    max="10"
                    required
                    className={touched.communicationScore && !feedbackForm.communicationScore ? 'error' : ''}
                  />
                  {touched.communicationScore && !feedbackForm.communicationScore && (
                    <span className="error-message">Please provide a rating (1-10)</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* DSA-specific fields */}
            {interviewType === 'DSA' && (
              <div className="form-section">
                <h4>DSA-Specific Evaluation</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Coding Ability (1-10) *</label>
                    <input
                      type="number"
                      name="codingAbility"
                      value={feedbackForm.codingAbility}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, codingAbility: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.codingAbility && !feedbackForm.codingAbility ? 'error' : ''}
                    />
                    {touched.codingAbility && !feedbackForm.codingAbility && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Algorithmic Thinking (1-10) *</label>
                    <input
                      type="number"
                      name="algorithmicThinking"
                      value={feedbackForm.algorithmicThinking}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, algorithmicThinking: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.algorithmicThinking && !feedbackForm.algorithmicThinking ? 'error' : ''}
                    />
                    {touched.algorithmicThinking && !feedbackForm.algorithmicThinking && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Problem Solving Approach (1-10) *</label>
                    <input
                      type="number"
                      name="problemSolvingApproach"
                      value={feedbackForm.problemSolvingApproach}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, problemSolvingApproach: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.problemSolvingApproach && !feedbackForm.problemSolvingApproach ? 'error' : ''}
                    />
                    {touched.problemSolvingApproach && !feedbackForm.problemSolvingApproach && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Time/Space Complexity Analysis (1-10) *</label>
                    <input
                      type="number"
                      name="timeComplexityAnalysis"
                      value={feedbackForm.timeComplexityAnalysis}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, timeComplexityAnalysis: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.timeComplexityAnalysis && !feedbackForm.timeComplexityAnalysis ? 'error' : ''}
                    />
                    {touched.timeComplexityAnalysis && !feedbackForm.timeComplexityAnalysis && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* System Design-specific fields */}
            {interviewType === 'System Design' && (
              <div className="form-section">
                <h4>System Design-Specific Evaluation</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Requirements Gathering (1-10) *</label>
                    <input
                      type="number"
                      name="requirementsGathering"
                      value={feedbackForm.requirementsGathering}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, requirementsGathering: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.requirementsGathering && !feedbackForm.requirementsGathering ? 'error' : ''}
                    />
                    {touched.requirementsGathering && !feedbackForm.requirementsGathering && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Architecture Design (1-10) *</label>
                    <input
                      type="number"
                      name="architectureDesign"
                      value={feedbackForm.architectureDesign}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, architectureDesign: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.architectureDesign && !feedbackForm.architectureDesign ? 'error' : ''}
                    />
                    {touched.architectureDesign && !feedbackForm.architectureDesign && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Scalability Considerations (1-10) *</label>
                    <input
                      type="number"
                      name="scalabilityConsiderations"
                      value={feedbackForm.scalabilityConsiderations}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, scalabilityConsiderations: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.scalabilityConsiderations && !feedbackForm.scalabilityConsiderations ? 'error' : ''}
                    />
                    {touched.scalabilityConsiderations && !feedbackForm.scalabilityConsiderations && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Tradeoff Analysis (1-10) *</label>
                    <input
                      type="number"
                      name="tradeoffAnalysis"
                      value={feedbackForm.tradeoffAnalysis}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, tradeoffAnalysis: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.tradeoffAnalysis && !feedbackForm.tradeoffAnalysis ? 'error' : ''}
                    />
                    {touched.tradeoffAnalysis && !feedbackForm.tradeoffAnalysis && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Behavioral/Leadership-specific fields */}
            {interviewType === 'Behavioral/Leadership' && (
              <div className="form-section">
                <h4>Behavioral/Leadership-Specific Evaluation</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Situational Awareness (1-10) *</label>
                    <input
                      type="number"
                      name="situationalAwareness"
                      value={feedbackForm.situationalAwareness}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, situationalAwareness: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.situationalAwareness && !feedbackForm.situationalAwareness ? 'error' : ''}
                    />
                    {touched.situationalAwareness && !feedbackForm.situationalAwareness && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Leadership Skills (1-10) *</label>
                    <input
                      type="number"
                      name="leadershipSkills"
                      value={feedbackForm.leadershipSkills}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, leadershipSkills: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.leadershipSkills && !feedbackForm.leadershipSkills ? 'error' : ''}
                    />
                    {touched.leadershipSkills && !feedbackForm.leadershipSkills && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Teamwork Ability (1-10) *</label>
                    <input
                      type="number"
                      name="teamworkAbility"
                      value={feedbackForm.teamworkAbility}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, teamworkAbility: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.teamworkAbility && !feedbackForm.teamworkAbility ? 'error' : ''}
                    />
                    {touched.teamworkAbility && !feedbackForm.teamworkAbility && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Conflict Resolution (1-10) *</label>
                    <input
                      type="number"
                      name="conflictResolution"
                      value={feedbackForm.conflictResolution}
                      onChange={handleChange}
                      onBlur={() => setTouched({...touched, conflictResolution: true})}
                      min="1"
                      max="10"
                      required
                      className={touched.conflictResolution && !feedbackForm.conflictResolution ? 'error' : ''}
                    />
                    {touched.conflictResolution && !feedbackForm.conflictResolution && (
                      <span className="error-message">Please provide a rating (1-10)</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Common text fields for all interview types */}
            <div className="form-section">
              <h4>Detailed Feedback</h4>
              <div className="form-group">
                <label>Strengths *</label>
                <textarea
                  name="strengths"
                  value={feedbackForm.strengths}
                  onChange={handleChange}
                  placeholder="What did the candidate do well?"
                  rows={3}
                  required
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Areas of Improvement *</label>
                <textarea
                  name="areasOfImprovement"
                  value={feedbackForm.areasOfImprovement}
                  onChange={handleChange}
                  placeholder="What could the candidate improve on?"
                  rows={3}
                  required
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Additional Comments</label>
                <textarea
                  name="additionalComments"
                  value={feedbackForm.additionalComments}
                  onChange={handleChange}
                  placeholder="Any other observations or comments"
                  rows={3}
                ></textarea>
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : isEditing ? 'Update Feedback' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InterviewFeedback;
